import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

/* ─── Design tokens (matching Admin.tsx) ─── */
const T = {
  navy:      "#002868",
  gold:      "#C5A55A",
  goldLight: "#D4B97A",
  red:       "#B22234",
  text:      "#1B1B1B",
  textMuted: "rgba(27,27,27,0.62)",
  border:    "rgba(27,27,27,0.12)",
  cardBg:    "#FFFFFF",
  shadow:    "0 2px 16px rgba(0,0,0,0.07)",
};

const sans = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

interface VoiceConfig {
  id: string;
  language: string;
  language_name: string;
  elevenlabs_voice_id: string;
  voice_name: string | null;
  voice_model: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface NarrationStats {
  total_lines: number;
  lines_with_audio: number;
  scenarios_with_narration: number;
  codes_with_narration: number;
}

export function DignityVoiceManager() {
  const [configs, setConfigs] = useState<VoiceConfig[]>([]);
  const [stats, setStats] = useState<NarrationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null); // id of config being edited
  const [form, setForm] = useState({ language: "", language_name: "", elevenlabs_voice_id: "", voice_name: "", voice_model: "eleven_turbo_v2_5" });
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchData = async () => {
    setLoading(true);

    // Fetch voice configs
    const { data: voiceData } = await supabase
      .from("dignity_voice_config")
      .select("*")
      .order("language");

    // Fetch narration stats
    const { data: lineData } = await supabase
      .from("dignity_narration_lines")
      .select("id, scenario_id, procedure_code, audio_url, is_active")
      .eq("is_active", true);

    const lines = lineData || [];
    const statsObj: NarrationStats = {
      total_lines: lines.length,
      lines_with_audio: lines.filter(l => l.audio_url).length,
      scenarios_with_narration: new Set(lines.filter(l => l.scenario_id).map(l => l.scenario_id)).size,
      codes_with_narration: new Set(lines.filter(l => l.procedure_code).map(l => l.procedure_code)).size,
    };

    setConfigs((voiceData as VoiceConfig[]) || []);
    setStats(statsObj);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (isNew: boolean) => {
    setSaving(true);
    if (isNew) {
      await supabase.from("dignity_voice_config").insert({
        language: form.language,
        language_name: form.language_name,
        elevenlabs_voice_id: form.elevenlabs_voice_id,
        voice_name: form.voice_name || null,
        voice_model: form.voice_model,
      });
    } else if (editing) {
      await supabase.from("dignity_voice_config").update({
        language_name: form.language_name,
        elevenlabs_voice_id: form.elevenlabs_voice_id,
        voice_name: form.voice_name || null,
        voice_model: form.voice_model,
        updated_at: new Date().toISOString(),
      }).eq("id", editing);
    }
    setSaving(false);
    setEditing(null);
    setShowAddForm(false);
    setForm({ language: "", language_name: "", elevenlabs_voice_id: "", voice_name: "", voice_model: "eleven_turbo_v2_5" });
    fetchData();
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    await supabase.from("dignity_voice_config").update({
      is_active: !currentActive,
      updated_at: new Date().toISOString(),
    }).eq("id", id);
    fetchData();
  };

  const startEdit = (config: VoiceConfig) => {
    setEditing(config.id);
    setShowAddForm(false);
    setForm({
      language: config.language,
      language_name: config.language_name,
      elevenlabs_voice_id: config.elevenlabs_voice_id,
      voice_name: config.voice_name || "",
      voice_model: config.voice_model,
    });
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: sans,
    fontSize: 13,
    padding: "8px 12px",
    border: `1px solid ${T.border}`,
    borderRadius: 6,
    width: "100%",
    outline: "none",
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontFamily: sans }}>Loading voice configuration...</div>;
  }

  return (
    <div style={{ fontFamily: sans }}>

      {/* Stats Dashboard */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16, marginBottom: 32 }}>
        {[
          { label: "Total Lines", value: stats?.total_lines ?? 0, color: T.navy },
          { label: "With Audio", value: stats?.lines_with_audio ?? 0, color: "#2b5e49" },
          { label: "Scenarios", value: stats?.scenarios_with_narration ?? 0, color: T.gold },
          { label: "Code Pages", value: stats?.codes_with_narration ?? 0, color: T.red },
        ].map(stat => (
          <div key={stat.label} style={{
            background: T.cardBg, borderRadius: 10, padding: "16px 20px",
            boxShadow: T.shadow, border: `1px solid ${T.border}`,
          }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: stat.color, fontFamily: serif }}>{stat.value}</div>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 4 }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Voice Configurations Table */}
      <div style={{
        background: T.cardBg, borderRadius: 12, boxShadow: T.shadow,
        border: `1px solid ${T.border}`, overflow: "hidden",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "16px 20px", borderBottom: `1px solid ${T.border}`,
        }}>
          <h3 style={{ fontFamily: serif, fontSize: 18, fontWeight: 400, color: T.navy, margin: 0 }}>
            Voice Configurations
          </h3>
          <button
            onClick={() => {
              setShowAddForm(true);
              setEditing(null);
              setForm({ language: "", language_name: "", elevenlabs_voice_id: "", voice_name: "", voice_model: "eleven_turbo_v2_5" });
            }}
            style={{
              fontFamily: sans, fontSize: 12, fontWeight: 600,
              padding: "6px 16px", borderRadius: 6,
              background: T.navy, color: "#fff", border: "none", cursor: "pointer",
            }}
          >
            + Add Language
          </button>
        </div>

        {/* Add/Edit Form */}
        {(showAddForm || editing) && (
          <div style={{ padding: "16px 20px", background: "rgba(0,40,104,0.03)", borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr 1fr 1fr", gap: 12, alignItems: "end" }}>
              <div>
                <label style={{ fontSize: 11, color: T.textMuted, display: "block", marginBottom: 4 }}>Language Code</label>
                <input
                  style={{ ...inputStyle, ...(editing ? { background: "#f0f0f0" } : {}) }}
                  value={form.language}
                  onChange={e => setForm(f => ({ ...f, language: e.target.value }))}
                  placeholder="en"
                  disabled={!!editing}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: T.textMuted, display: "block", marginBottom: 4 }}>Language Name</label>
                <input
                  style={inputStyle}
                  value={form.language_name}
                  onChange={e => setForm(f => ({ ...f, language_name: e.target.value }))}
                  placeholder="English"
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: T.textMuted, display: "block", marginBottom: 4 }}>ElevenLabs Voice ID</label>
                <input
                  style={inputStyle}
                  value={form.elevenlabs_voice_id}
                  onChange={e => setForm(f => ({ ...f, elevenlabs_voice_id: e.target.value }))}
                  placeholder="pNInz6obpgDQGcFmaJgB"
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: T.textMuted, display: "block", marginBottom: 4 }}>Voice Name</label>
                <input
                  style={inputStyle}
                  value={form.voice_name}
                  onChange={e => setForm(f => ({ ...f, voice_name: e.target.value }))}
                  placeholder="Daniel"
                />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => handleSave(showAddForm && !editing)}
                  disabled={saving || !form.language || !form.language_name || !form.elevenlabs_voice_id}
                  style={{
                    fontFamily: sans, fontSize: 12, fontWeight: 600,
                    padding: "8px 14px", borderRadius: 6,
                    background: saving ? T.textMuted : "#2b5e49", color: "#fff",
                    border: "none", cursor: saving ? "default" : "pointer", whiteSpace: "nowrap",
                  }}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={() => { setEditing(null); setShowAddForm(false); }}
                  style={{
                    fontFamily: sans, fontSize: 12, padding: "8px 14px", borderRadius: 6,
                    background: "transparent", color: T.textMuted,
                    border: `1px solid ${T.border}`, cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 11, color: T.textMuted, display: "block", marginBottom: 4 }}>Model</label>
              <select
                style={{ ...inputStyle, maxWidth: 280 }}
                value={form.voice_model}
                onChange={e => setForm(f => ({ ...f, voice_model: e.target.value }))}
              >
                <option value="eleven_v3">eleven_v3 (latest, most expressive)</option>
                <option value="eleven_turbo_v2_5">eleven_turbo_v2_5 (fast)</option>
                <option value="eleven_multilingual_v2">eleven_multilingual_v2 (multilingual)</option>
              </select>
            </div>
          </div>
        )}

        {/* Table */}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {["Language", "Voice ID", "Voice Name", "Model", "Status", "Actions"].map(h => (
                <th key={h} style={{
                  padding: "10px 16px", textAlign: "left",
                  fontSize: 11, fontWeight: 700, color: T.textMuted,
                  textTransform: "uppercase", letterSpacing: "0.5px",
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {configs.map(c => (
              <tr key={c.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{ fontWeight: 700, color: T.navy }}>{c.language.toUpperCase()}</span>
                  <span style={{ color: T.textMuted, fontSize: 12, marginLeft: 8 }}>{c.language_name}</span>
                </td>
                <td style={{ padding: "12px 16px", fontFamily: "monospace", fontSize: 12, color: T.textMuted }}>
                  {c.elevenlabs_voice_id.length > 20 ? c.elevenlabs_voice_id.slice(0, 18) + "..." : c.elevenlabs_voice_id}
                </td>
                <td style={{ padding: "12px 16px", fontSize: 13 }}>{c.voice_name || "—"}</td>
                <td style={{ padding: "12px 16px", fontSize: 12, color: T.textMuted }}>{c.voice_model}</td>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{
                    display: "inline-block", padding: "3px 10px", borderRadius: 12,
                    fontSize: 11, fontWeight: 600,
                    background: c.is_active ? "rgba(43,94,73,0.1)" : "rgba(178,34,52,0.1)",
                    color: c.is_active ? "#2b5e49" : T.red,
                  }}>
                    {c.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => startEdit(c)}
                      style={{
                        fontSize: 12, padding: "4px 10px", borderRadius: 4,
                        background: "transparent", color: T.navy,
                        border: `1px solid ${T.border}`, cursor: "pointer", fontFamily: sans,
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(c.id, c.is_active)}
                      style={{
                        fontSize: 12, padding: "4px 10px", borderRadius: 4,
                        background: "transparent",
                        color: c.is_active ? T.red : "#2b5e49",
                        border: `1px solid ${T.border}`, cursor: "pointer", fontFamily: sans,
                      }}
                    >
                      {c.is_active ? "Disable" : "Enable"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {configs.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 32, textAlign: "center", color: T.textMuted, fontSize: 13 }}>
                  No voice configurations yet. Add one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pending audio section */}
      {stats && stats.total_lines > 0 && stats.lines_with_audio < stats.total_lines && (
        <div style={{
          marginTop: 24, padding: "16px 20px", borderRadius: 10,
          background: "rgba(197,165,90,0.08)", border: `1px solid rgba(197,165,90,0.2)`,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.gold, marginBottom: 4 }}>
            Audio Generation Pending
          </div>
          <div style={{ fontSize: 12, color: T.textMuted }}>
            {stats.total_lines - stats.lines_with_audio} of {stats.total_lines} narration lines are waiting for TTS audio generation.
            Run <code style={{ background: "rgba(0,0,0,0.05)", padding: "2px 6px", borderRadius: 3 }}>scripts/generate_narration_audio.py</code> to process them.
          </div>
        </div>
      )}
    </div>
  );
}
